import type { Friend } from '../types/create.types';

export interface FriendWithInvitation extends Friend {
  friendshipStatus?: 'pending' | 'accepted';
  invitationType?: 'sent' | 'received';
  inviteeId?: string;
  inviterId?: string;
  invitationId?: string;
  email?: string;
}

export interface ResolvedFriend {
  friend: Friend;
  userId: string;
  isNonRegistered: boolean;
  email?: string;
  needsFriendInvitation?: boolean;
}

export interface ResolveFriendError {
  type: 'PENDING_FRIEND' | 'INCOMPLETE_DATA' | 'UNKNOWN';
  message: string;
  targetUserId?: string;
  targetFriendName?: string;
}

export const resolveFriendUserId = (
  friend: Friend,
  friendId: string
): {
  userId: string | null;
  isNonRegistered: boolean;
  email?: string;
  needsFriendInvitation?: boolean;
  error?: ResolveFriendError;
} => {
  const friendWithInvitation = friend as FriendWithInvitation;

  if (!friend || (friendId === null || friendId === undefined)) {
    return {
      userId: null,
      isNonRegistered: false,
      error: {
        type: 'INCOMPLETE_DATA',
        message: 'Invalid friend data provided.',
      },
    };
  }

  if (friendId.startsWith('pending_') || (friend.id && friend.id.startsWith('pending_'))) {
    if (friendWithInvitation.invitationType === 'sent' && friendWithInvitation.inviteeId) {
      return { userId: friendWithInvitation.inviteeId, isNonRegistered: false };
    }
    if (friendWithInvitation.invitationType === 'received' && friendWithInvitation.inviterId) {
      return { userId: friendWithInvitation.inviterId, isNonRegistered: false };
    }
    if (friendWithInvitation.email) {
      return {
        userId: null,
        isNonRegistered: true,
        email: friendWithInvitation.email,
        needsFriendInvitation: !friendWithInvitation.invitationId,
      };
    }
    return {
      userId: null,
      isNonRegistered: false,
      error: {
        type: 'INCOMPLETE_DATA',
        message: `Cannot create challenge with ${friend.name} - pending invitation information is incomplete. Please refresh your friends list and try again.`,
        targetUserId: friend.id || friendId,
        targetFriendName: friend.name,
      },
    };
  }

  if (friendWithInvitation.friendshipStatus === 'accepted') {
    return { userId: friend.id, isNonRegistered: false };
  }

  if (friendWithInvitation.friendshipStatus === 'pending') {
    let actualUserId: string | undefined;

    if (friendWithInvitation.invitationType === 'sent' && friendWithInvitation.inviteeId) {
      actualUserId = friendWithInvitation.inviteeId;
    } else if (friendWithInvitation.invitationType === 'received' && friendWithInvitation.inviterId) {
      actualUserId = friendWithInvitation.inviterId;
    } else if (friend.id && friend.id.trim() !== '') {
      const isTemporaryId = friend.id.startsWith('pending_') || friend.id.startsWith('pending-');
      if (!isTemporaryId) {
        actualUserId = friend.id;
      } else if (!friendWithInvitation.email) {
        actualUserId = friend.id;
      }
    }

    if (actualUserId) {
      return { userId: actualUserId, isNonRegistered: false };
    }

    if (friendWithInvitation.email) {
      return {
        userId: null,
        isNonRegistered: true,
        email: friendWithInvitation.email,
        needsFriendInvitation: !friendWithInvitation.invitationId,
      };
    }

    return {
      userId: null,
      isNonRegistered: false,
      error: {
        type: 'INCOMPLETE_DATA',
        message: `Cannot create challenge with ${friend.name} - pending invitation information is incomplete. Please refresh your friends list and try again.`,
        targetUserId: friend.id,
        targetFriendName: friend.name,
      },
    };
  }

  if (friendId.trim() === '' && friend.id && friend.id.trim() !== '') {
    return {
      userId: null,
      isNonRegistered: false,
      error: {
        type: 'INCOMPLETE_DATA',
        message: 'Invalid friend data provided.',
      },
    };
  }

  if ((!friend.id || friend.id.trim() === '') && friendWithInvitation.email) {
    return {
      userId: null,
      isNonRegistered: true,
      email: friendWithInvitation.email,
      needsFriendInvitation: true,
    };
  }

  if (
    friend.id &&
    friend.id.trim() !== '' &&
    friendId.trim() !== '' &&
    friendId === friend.id &&
    !friendWithInvitation.friendshipStatus &&
    !friendWithInvitation.email
  ) {
    if (friend.id.startsWith('pending_')) {
      return {
        userId: null,
        isNonRegistered: false,
        error: {
          type: 'INCOMPLETE_DATA',
          message: `Cannot create challenge with ${friend.name} - user information is incomplete. Please refresh your friends list and try again.`,
          targetUserId: friend.id,
          targetFriendName: friend.name,
        },
      };
    }
    return { userId: friend.id, isNonRegistered: false };
  }

  return {
    userId: null,
    isNonRegistered: false,
    error: {
      type: 'INCOMPLETE_DATA',
      message: `Cannot create challenge with ${friend.name} - user information is incomplete.`,
      targetUserId: friend.id,
      targetFriendName: friend.name,
    },
  };
};
